import React, { useEffect, useRef, useState } from "react";
import { CygnusTooltip } from "reusable-components/CygnusTooltip/CygnusTooltip";
import { createPortal } from "react-dom";
import { ThreeCircles } from "react-loader-spinner";
import styles from "./ReturnIdDropdown.module.css";
import type { DestinationReturnIdOption, ReturnIdDropdownProps } from "./ReturnIdDropdown.types";
import {
  formatReturnLastUpdated,
  getDestinationReturnOptions,
  getStatusColors,
} from "./ReturnIdDropdown.utils";

export const ReturnIdDropdown: React.FC<ReturnIdDropdownProps> = ({
  value,
  clientMasterId,
  parentClientMasterId,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DestinationReturnIdOption[]>([]);
  const [loading, setLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const computeMenuStyle = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const menuMaxHeight = 300;
    const gap = 6;
    const minHeight = 80;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const showBelow = spaceBelow >= minHeight || spaceBelow >= spaceAbove;
    const maxHeight = showBelow
      ? Math.min(menuMaxHeight, Math.max(spaceBelow, minHeight))
      : Math.min(menuMaxHeight, spaceAbove);

    const width = Math.min(400, Math.max(280, rect.width));
    const left = Math.min(rect.left, window.innerWidth - width - 8);

    setMenuStyle({
      position: "fixed",
      top: showBelow ? rect.bottom + gap : rect.top - maxHeight - gap,
      left,
      width,
      maxHeight,
      zIndex: 10050,
    });
  };

  const handleToggle = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }

    setLoading(!hasFetchedRef.current);
    setOpen(true);
    computeMenuStyle();

    if (!hasFetchedRef.current) {
      try {
        const mapped = await getDestinationReturnOptions(clientMasterId, parentClientMasterId);
        setOptions(mapped);
        hasFetchedRef.current = true;
      } catch {
      } finally {
        setLoading(false);
        computeMenuStyle();
      }
    }
  };

  useEffect(() => {
    setOptions([]);
    hasFetchedRef.current = false;
  }, [clientMasterId, parentClientMasterId]);

  useEffect(() => {
    if (!value || hasFetchedRef.current) return;

    const preloadSelectedOptions = async () => {
      try {
        const mapped = await getDestinationReturnOptions(clientMasterId, parentClientMasterId);
        setOptions(mapped);
        hasFetchedRef.current = true;
      } catch {
      }
    };

    void preloadSelectedOptions();
  }, [value, clientMasterId, parentClientMasterId]);

  useEffect(() => {
    if (!open) return;

    const close = () => setOpen(false);

    const onOutside = (event: MouseEvent) => {
      if (
        !triggerRef.current?.contains(event.target as Node) &&
        !menuRef.current?.contains(event.target as Node)
      ) {
        close();
      }
    };

    const onScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      close();
    };

    const onWheel = (event: WheelEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      close();
    };

    document.addEventListener("mousedown", onOutside, true);
    document.addEventListener("scroll", onScroll, true);
    document.addEventListener("wheel", onWheel, true);
    return () => {
      document.removeEventListener("mousedown", onOutside, true);
      document.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("wheel", onWheel, true);
    };
  }, [open]);

  return (
    <div
      className={styles.returnIdDropdownWrapper}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.returnIdDropdownTrigger} ${open ? styles.returnIdDropdownTriggerOpen : ""}`}
        onClick={handleToggle}
      >
        <span className={value ? styles.returnIdDropdownValue : styles.returnIdDropdownPlaceholder}>
          {value || "Select Return ID"}
        </span>
        {value && (
          <span
            className={styles.returnIdDropdownClear}
            role="button"
            aria-label="Clear selection"
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
          >
            ×
          </span>
        )}
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`${styles.returnIdDropdownChevron} ${open ? styles.returnIdDropdownChevronOpen : ""}`}
        >
          <path
            d="M2.5 4L6 7.5L9.5 4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open &&
        createPortal(
          <div ref={menuRef} style={menuStyle} className={styles.returnIdDropdownMenu}>
            <div className={styles.returnIdDropdownHeader}>
              <span className={styles.returnIdDropdownHeaderText}>Select Return ID</span>
              <span className={styles.returnIdDropdownHeaderCount}>
                {!loading && `${options.length} available`}
              </span>
            </div>
            {loading ? (
              <div className={styles.returnIdDropdownLoader}>
                <ThreeCircles
                  height="20"
                  width="20"
                  visible
                  ariaLabel="loading-return-ids"
                  outerCircleColor="var(--theme1_primary)"
                  innerCircleColor="var(--theme1_secondary)"
                  middleCircleColor="var(--theme1_tertiary)"
                />
                <span className={styles.returnIdDropdownLoaderText}>Loading return IDs...</span>
              </div>
            ) : options.length === 0 ? (
              <div className={styles.returnIdDropdownEmpty}>No return IDs available</div>
            ) : (
              <div className={styles.returnIdDropdownList}>
                {options.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <div
                      key={option.value}
                      className={`${styles.returnIdDropdownOption} ${
                        isSelected ? styles.returnIdDropdownOptionSelected : ""
                      }`}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={() => {
                        onChange(option);
                        setOpen(false);
                      }}
                    >
                      <div className={styles.returnIdOptionMeta}>
                        last updated on : {formatReturnLastUpdated(option.lastUpdated)}
                      </div>
                      <div className={styles.returnIdOptionRow}>
                        <CygnusTooltip text={option.label}>
                          <span className={styles.returnIdOptionLabel}>{option.label}</span>
                        </CygnusTooltip>
                        <div className={styles.returnIdOptionRight}>
                          {option.status &&
                            (() => {
                              const statusColors = getStatusColors(option.status);
                              return (
                                <span
                                  className={styles.returnIdStatusPill}
                                  style={
                                    {
                                      "--pill-bg": statusColors.bg,
                                      "--pill-border": statusColors.border,
                                      "--pill-fg": statusColors.fg,
                                    } as React.CSSProperties
                                  }
                                >
                                  <span
                                    className={styles.returnIdStatusDot}
                                    style={{ background: statusColors.dot }}
                                  />
                                  {option.status}
                                </span>
                              );
                            })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>,
          (document.fullscreenElement as HTMLElement) ?? document.body,
        )}
    </div>
  );
};
